/**
 * @fileoverview Shared utility functions for the Events UI app.
 * Includes date/time formatting, session/URL helpers, pricing, validation, and child/event utilities.
 */
import moment from 'moment';
import { ALL_EVENTS_GROUP, DATE_TIME_FORMAT, SESSION_KEY_CLIENT_ID, SESSION_KEY_GROUP_ID, SESSION_KEY_LOCATION_ID, SESSION_KEY_USER_ORDERS_DATA, SESSION_KEY_UTM_PARAMS } from '../constants';
import { CartItem, Child, EventDetail, PricingDiscountTypes } from '../types';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(isBetween);
dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * Calculates a person's age in full years based on their date of birth.
 * @param dob - Date of birth string (parseable by Date constructor)
 * @returns Age in full years
 */
export function calculateAge(dob: string): number {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

/**
 * Calculates a person's age in months based on their date of birth.
 * @param dob - Date of birth string (parseable by dayjs)
 * @returns Age in months (can be fractional)
 */
export function calculateAgeInMonths(dob: string): number {
  const birthDate = dayjs(dob);
  const now = dayjs();
  return now.diff(birthDate, 'month', true);
}

/**
 * Converts a DOB string in any supported format (MM/DD/YYYY or M/D/YYYY) to
 * the API-canonical ISO format YYYY-MM-DD expected by events_api age validation.
 * Returns the input unchanged if it is already YYYY-MM-DD or unrecognised.
 */
export function formatDobForApi(dob: string): string {
  const trimmed = dob?.trim() ?? '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (m) {
    const [, month, day, year] = m;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return trimmed;
}

/**
 * Converts a DOB string returned by Parents API (YYYY-MM-DD) to the
 * display format MM/DD/YYYY used throughout the UI.
 * Returns the input unchanged if it is already in slash format or unrecognised.
 */
export function formatDobForDisplay(dob: string): string {
  const trimmed = dob?.trim() ?? '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (m) {
    const [, year, month, day] = m;
    return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
  }
  return trimmed;
}

/**
 * Gets the client ID from URL query params or localStorage.
 * If URL has a different client_id than stored, clears all localStorage.
 * Redirects to kindertales.com if no client ID exists (except on kids-co routes).
 * @returns The client ID string, or null/undefined if none exists
 */
export function getClientId() {
  const urlParams = new URLSearchParams(window.location.search);
  const clientIdFromUrl = urlParams.get(SESSION_KEY_CLIENT_ID);

  if (clientIdFromUrl) {
    // If new Client ID, then we clear the session
    if (clientIdFromUrl != localStorage.getItem(SESSION_KEY_CLIENT_ID)) {
      localStorage.clear();
    }

    localStorage.setItem(SESSION_KEY_CLIENT_ID, clientIdFromUrl);
    return clientIdFromUrl;
  } else {
    const clientIdFromStorage = localStorage.getItem(SESSION_KEY_CLIENT_ID);
    if (!clientIdFromStorage && !window.location.pathname.includes('kids-co')) {
      window.location.href = 'https://kindertales.com';
    }

    return clientIdFromStorage;
  }
}

/**
 * Gets the event group ID from URL query params (group_id).
 * Persists the value to localStorage when found in URL.
 * @returns The group ID as a number, or 0 if not present
 */
export function getGroupId(): number {
  const urlParams = new URLSearchParams(window.location.search);
  const groupIdFromUrl = urlParams.get(SESSION_KEY_GROUP_ID);

  if (groupIdFromUrl) {
    localStorage.setItem(SESSION_KEY_GROUP_ID, groupIdFromUrl);
    return groupIdFromUrl as unknown as number;
  }
  return 0;
}

/**
 * Gets the location ID from URL query params or localStorage.
 * If URL has a different location_id than stored, clears localStorage (preserving client_id).
 * Redirects to kindertales.com if no location ID exists (except on kids-co routes).
 * @returns The location ID string, or null/undefined if none exists
 */
export function getLocationId() {
  const urlParams = new URLSearchParams(window.location.search);
  const locationIdFromUrl = urlParams.get(SESSION_KEY_LOCATION_ID);

  if (locationIdFromUrl) {
    // If new Location ID, then we clear the session
    if (locationIdFromUrl != localStorage.getItem(SESSION_KEY_LOCATION_ID)) {
      const clientIdFromStorage = localStorage.getItem(SESSION_KEY_CLIENT_ID) as string;
      localStorage.clear();
      localStorage.setItem(SESSION_KEY_CLIENT_ID, clientIdFromStorage);
    }

    localStorage.setItem(SESSION_KEY_LOCATION_ID, locationIdFromUrl);
    return locationIdFromUrl;
  } else {
    const locationIdFromStorage = localStorage.getItem(SESSION_KEY_LOCATION_ID);

    if (!locationIdFromStorage && !window.location.pathname.includes('kids-co')) {
      window.location.href = 'https://kindertales.com';
    }

    return locationIdFromStorage;
  }
}

/**
 * Converts a 24-hour time string to 12-hour AM/PM format.
 * @param timeString - Time in HH:mm format
 * @returns Formatted time string (e.g. "9:30 AM")
 */
export function convertTime(timeString: string) {
  return moment(timeString, 'HH:mm').format('h:mm A');
};

/**
 * Calculates the final price after applying a discount and optional coupon.
 * @param chargePerPeriod - Base charge amount
 * @param discountType - Type of discount (FIXED or PERCENTAGE)
 * @param discountAmount - Discount value (fixed amount or percentage)
 * @param couponDiscountAmount - Optional coupon discount to apply first
 * @returns Final price as a string with 2 decimal places
 */
export const calculateDiscountedPrice = (
  chargePerPeriod: number | string | undefined,
  discountType: PricingDiscountTypes | undefined,
  discountAmount: number | string | undefined,
  couponDiscountAmount: number | string | undefined
): string => {
  let charge = parseFloat(chargePerPeriod ? chargePerPeriod.toString() : '0');
  const discount = parseFloat(discountAmount ? discountAmount.toString() : '0');
  const couponDiscount = parseFloat(couponDiscountAmount ? couponDiscountAmount.toString() : '0');

  if (couponDiscountAmount) {
    charge = charge - couponDiscount;
  }

  let finalPrice = charge;
  if (discountType === PricingDiscountTypes.FIXED) {
    finalPrice = charge - discount;
  } else if (discountType === PricingDiscountTypes.PERCENTAGE) {
    finalPrice = charge * (1 - discount / 100);
  }

  if (finalPrice < 0) {
    finalPrice = 0;
  }

  return finalPrice.toFixed(2);
};

/**
 * Determines the registration status of an event for a given child.
 * @param event - Event detail object
 * @param childId - Optional child ID to check registration
 * @returns Object with status ('Open' | 'Closed' | 'Registered' | 'Opening Soon') and className for styling
 */
export const getEventStatus = (event: EventDetail, childId: number | undefined) => {
  const currentDate = new Date();
  const startDate = new Date(event.registrationStartDate);
  const endDate = new Date(event.registrationEndDate);

  if (event.totalOccupiedSeats >= event.capacity) {
    return { status: 'Closed', className: 'closed' };
  }

  if (!event.pricing || event.pricing.length === 0) {
    return { status: 'Closed', className: 'closed' };
  }

  // Check if the child is registered for this event
  const userOrdersData = localStorage.getItem(SESSION_KEY_USER_ORDERS_DATA);
  if (childId && userOrdersData) {
      const orders = JSON.parse(userOrdersData);

      if (orders[childId] && orders[childId].includes(event.id)) {
          return { status: 'Registered', className: 'registered' };
      }
  }

  const isProgramEnrollmentExist = event.programEnrollments?.find(p => p.childId === childId);

  if (isProgramEnrollmentExist) {
    return { status: 'Registered', className: 'registered' };
  }

  if (currentDate < startDate) {
      return { status: 'Opening Soon', className: 'opening-soon' };
  } if (currentDate > endDate) {
      return { status: 'Closed', className: 'closed' };
  }
  return { status: 'Open', className: 'open' };
};

/**
 * Sums the coupon discount amounts across all cart items.
 * @param items - Cart items object with data array containing discountAmountCoupon
 * @returns Total coupon discount amount, or 0 if items invalid
 */
export const calculateTotalCouponDiscount = (items: any): number => {
  if (!items || !items.data || !Array.isArray(items.data)) return 0;
  
  return items.data.reduce((acc: any, item: { discountAmountCoupon: number; }) => {
    const discount = typeof item.discountAmountCoupon === 'number' 
      ? item.discountAmountCoupon 
      : parseFloat(item.discountAmountCoupon || '0');
    return acc + (isNaN(discount) ? 0 : discount);
  }, 0);
};

/**
 * Safely parses a value to a number, returning a default if invalid.
 * @param value - Value to parse (string, number, null, or undefined)
 * @param defaultValue - Value to return when parsing fails (default: 0)
 * @returns Parsed number or defaultValue
 */
export const safeParseFloat = (
  value: string | number | null | undefined,
  defaultValue: number = 0
): number => {
  if (value === null || value === undefined || value === '') return defaultValue;
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(num) ? defaultValue : num;
};

/**
 * Formats a numeric value for currency display (2 decimal places).
 * @param value - Numeric value to format
 * @returns Formatted string (e.g. "12.50" or "0.00")
 */
export const formatCurrencyDisplay = (
  value: number | string | null | undefined
): string => {
  const num = safeParseFloat(value);
  return num > 0 ? num.toFixed(2) : '0.00';
};

/**
 * Maps a Child object to the API payload format for registration.
 * @param child - Child object with fname, lname, dob, gender
 * @param locationOverride - Optional location ID to use instead of localStorage
 * @returns API-formatted object with firstname, lastname, dob, gender, location
 */
export const mapChildToApiFormat = (
  child: Child,
  locationOverride?: string | null
) => {
  return {
    firstname: child.fname,
    lastname: child.lname,
    dob: new Date(child.dob).toLocaleDateString('en-GB'),
    gender: child.gender,
    location: locationOverride ?? localStorage.getItem(SESSION_KEY_LOCATION_ID),
  };
};

/**
 * Validates password strength: at least 8 chars with digit, special char, lowercase, uppercase.
 * @param str - Password string to validate
 * @returns true if password meets requirements
 */
export function isValidPassword(str: string): boolean {
  let re = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
  return re.test(str);
}

/**
 * Formats a date string for display.
 * @param date - Date string (expected format: DATE_TIME_FORMAT)
 * @param format - Optional dayjs format string (default: 'M/D/YYYY')
 * @returns Formatted date string
 */
export const formatDate = (date: string, format?: string) => {
  return dayjs(date, DATE_TIME_FORMAT).format(format ?? 'M/D/YYYY');
};

/**
 * Finds children who have items in the cart for events that overlap in dates.
 * Used to warn about potential double-booking.
 * @param cartItem - Array of cart items
 * @returns Map of child ID to cart items where the child has overlapping event dates
 */
export const getChildrenInMultipleEventsAtTheSameTime = (cartItem: CartItem[]) => {
  dayjs.extend(isBetween)

  let childrenEvents: { [key: number]: CartItem[] } = {};
  let childrenWithEventsAtTheSameTime: { [key: number]: CartItem[] } = {};

  cartItem.forEach(item => {
    if (childrenEvents[item.child.id]) {
      if (childrenEvents[item.child.id].length) {
        childrenEvents[item.child.id].forEach(childItem => {
          let childItemStartDate = dayjs(childItem.item.event.startDate, 'YYYY-MM-DD');
          let childItemEndDate = dayjs(childItem.item.event.endDate, 'YYYY-MM-DD');
          let cartItemStartDate = dayjs(item.item.event.startDate, 'YYYY-MM-DD');
          let cartItemEndDate = dayjs(item.item.event.endDate, 'YYYY-MM-DD');
          if (childItemStartDate.isBetween(cartItemStartDate, cartItemEndDate, 'days', '[]') || childItemEndDate.isBetween(cartItemStartDate, cartItemEndDate, 'days', '[]')) {
            if (childrenWithEventsAtTheSameTime[item.child.id]) {
              childrenWithEventsAtTheSameTime[item.child.id] = [...childrenEvents[item.child.id], item];
            } else {
              childrenWithEventsAtTheSameTime[item.child.id] = [...childrenEvents[item.child.id], item];
            }
          }
        })
        childrenEvents[item.child.id].push(item);
      }
    } else {
      childrenEvents[item.child.id] = [item];
    }
  })

  return childrenWithEventsAtTheSameTime;
}

/**
 * Capitalizes the first character of a string.
 * @param str - Input string
 * @returns String with first letter capitalized, or empty string if input is empty
 */
export const capitalizeFirstLetter = (str: string) => {
  if (str.length === 0) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Formats an age range from min/max months for display.
 * @param minMonths - Minimum age in months
 * @param maxMonths - Optional maximum age in months (if greater than min, shows range)
 * @returns Formatted string (e.g. "3y 0m - 5y 11m" or "3y 0m+")
 */
export function formatAgeRange(minMonths: number, maxMonths?: number | null): string {
  const minY = Math.floor(minMonths / 12);
  const minM = minMonths % 12;
  if (maxMonths != null && maxMonths > minMonths) {
    const maxY = Math.floor(maxMonths / 12);
    const maxM = maxMonths % 12;
    return `${minY}y ${minM}m - ${maxY}y ${maxM}m`;
  }
  return `${minY}y ${minM}m+`;
}

export type EventsEmptyMessageParams = {
  selectedChild: Child | null;
  selectedEventGroup: { id?: string | number; name?: string } | null;
  hasActiveSearch: boolean;
  emptyDueToAge: boolean;
};

/**
 * Builds the empty-state message for the /events listing.
 * Distinguishes search misses, age ineligibility, and truly empty groups.
 * Omits a group age-range clause (sampled tile data can understate real bounds).
 */
export function getEventsEmptyMessage({
  selectedChild,
  selectedEventGroup,
  hasActiveSearch,
  emptyDueToAge,
}: EventsEmptyMessageParams): string {
  if (hasActiveSearch) {
    return 'No events match your search criteria.';
  }

  if (emptyDueToAge && selectedChild) {
    const childName = `${selectedChild.fname} ${selectedChild.lname}`.trim();
    const isAllEvents =
      selectedEventGroup?.id == null ||
      String(selectedEventGroup.id) === ALL_EVENTS_GROUP.id;

    if (isAllEvents) {
      return `${childName} does not meet the age requirements for the available events.`;
    }

    const groupName = selectedEventGroup?.name?.trim() || 'this group';
    return `${childName} does not meet the age requirements for events in ${groupName}.`;
  }

  return 'No events available.';
}

/**
 * Tracks and saves UTM parameters from the URL to localStorage.
 * Updates the stored UTM params if they have changed.
 * @returns The UTM parameters object, or null if no UTM params are present
 */
export function trackUtmParams(): Record<string, string> | null {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  // Extract all parameters that start with 'utm_'
  urlParams.forEach((value, key) => {
    if (key.toLowerCase().startsWith('utm_')) {
      utmParams[key.toLowerCase()] = value;
    }
  });

  // If no UTM params found, return null
  if (Object.keys(utmParams).length === 0) {
    return null;
  }

  // Get existing UTM params from localStorage
  const existingUtmParamsStr = localStorage.getItem(SESSION_KEY_UTM_PARAMS);
  let existingUtmParams: Record<string, string> | null = null;

  if (existingUtmParamsStr) {
    try {
      existingUtmParams = JSON.parse(existingUtmParamsStr);
    } catch (e) {
      // If parsing fails, treat as if no existing params
      existingUtmParams = null;
    }
  }

  // Check if UTM params have changed
  const hasChanged = !existingUtmParams || 
    JSON.stringify(existingUtmParams) !== JSON.stringify(utmParams);

  // Save to localStorage if changed or if no existing params
  if (hasChanged) {
    localStorage.setItem(SESSION_KEY_UTM_PARAMS, JSON.stringify(utmParams));
  }

  return utmParams;
}

/**
 * Retrieves UTM parameters from localStorage.
 * @returns The UTM parameters object, or null if not found
 */
export function getUtmParams(): Record<string, string> | null {
  const utmParamsStr = localStorage.getItem(SESSION_KEY_UTM_PARAMS);
  
  if (!utmParamsStr) {
    return null;
  }

  try {
    return JSON.parse(utmParamsStr);
  } catch (e) {
    return null;
  }
}
